import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, fmtDateLong } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LEGAL_LINKS } from "@/components/SiteFooter";
import { Ban, Trash2, ShieldCheck, Mail, KeyRound } from "lucide-react";

export default function Settings() {
  const { user, logout, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [emailMsg, setEmailMsg] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [prefs, setPrefs] = useState({ shift_emails: true, digest_emails: true });
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdMsg, setPwdMsg] = useState(null);
  const [pwdBusy, setPwdBusy] = useState(false);

  const changePassword = async () => {
    if (pwdForm.next !== pwdForm.confirm) {
      setPwdMsg({ ok: false, text: "Both new password fields must match." });
      return;
    }
    setPwdBusy(true);
    setPwdMsg(null);
    try {
      const { data } = await api.post("/account/password", {
        current_password: pwdForm.current, new_password: pwdForm.next,
      });
      setPwdMsg({ ok: true, text: data.message });
      setPwdForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      setPwdMsg({ ok: false, text: apiError(e) });
    } finally {
      setPwdBusy(false);
    }
  };
  const [blocks, setBlocks] = useState([]);
  const [consents, setConsents] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [b, c] = await Promise.all([api.get("/blocks"), api.get("/consents/me")]);
    setBlocks(b.data);
    setConsents(c.data);
  };

  useEffect(() => {
    setPrefs({
      shift_emails: user?.shift_emails !== false,
      digest_emails: user?.digest_emails !== false,
    });
  }, [user]);

  useEffect(() => {
    load();
  }, []);

  const unblock = async (id) => {
    await api.delete(`/blocks/${id}`);
    load();
  };

  const savePrefs = async (next) => {
    setPrefs(next);
    setPrefsSaved(false);
    await api.post("/account/email-preferences", next);
    setPrefsSaved(true);
  };

  const changeEmail = async () => {
    setEmailBusy(true);
    setEmailMsg(null);
    try {
      const { data } = await api.post("/account/email", { email, password: pwd || undefined });
      setEmailMsg({ ok: true, text: `Your sign-in email is now ${data.email}.` });
      setEmail("");
      setPwd("");
      await refresh();
    } catch (e) {
      setEmailMsg({ ok: false, text: apiError(e) });
    } finally {
      setEmailBusy(false);
    }
  };

  const deleteAccount = async () => {
    setBusy(true);
    setError("");
    try {
      await api.delete("/account");
      await logout();
      window.location.replace("/");
    } catch (e) {
      setError(apiError(e));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="settings-page">
      <p className="overline">Settings</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
        Privacy & account settings
      </h1>

      <section className="card-surface mt-8 p-6" data-testid="blocked-users-section">
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold">
          <Ban className="h-4 w-4 text-slate-500" /> Blocked users
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Blocked users are hidden from your matches and cannot send you new requests.
        </p>
        {blocks.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500" data-testid="blocked-empty">
            You have not blocked anyone.
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100">
            {blocks.map((b) => (
              <li key={b.blocked_id} className="flex items-center justify-between py-3" data-testid={`block-row-${b.blocked_id}`}>
                <span className="text-sm">
                  <span className="font-medium">{b.name || "User"}</span>
                  <span className="ml-2 text-slate-400">{b.role}</span>
                </span>
                <button onClick={() => unblock(b.blocked_id)} className="text-sm font-medium text-brand" data-testid={`unblock-${b.blocked_id}`}>
                  Unblock
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card-surface mt-6 p-6" data-testid="consent-records-section">
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold">
          <ShieldCheck className="h-4 w-4 text-slate-500" /> Your legal acknowledgements
        </h2>
        {consents.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">No consent records found for this account.</p>
        ) : (
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            {consents.map((c) => (
              <li key={c.consent_id} data-testid={`consent-${c.consent_id}`}>
                Terms v{c.terms_version} · Privacy v{c.privacy_version} · Acceptable Use v
                {c.acceptable_use_version} — accepted {fmtDateLong(c.accepted_at?.slice(0, 10))}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
          {LEGAL_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="font-medium text-brand hover:underline">
              {l.label}
            </Link>
          ))}
        </div>
      </section>

      <section className="card-surface mt-6 p-6" data-testid="change-password-section">
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold">
          <KeyRound className="h-4 w-4 text-slate-500" /> Password
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Use at least 8 characters. Other devices will need the new password.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <label className="field-label" htmlFor="current-pwd">Current password</label>
            <input id="current-pwd" className="field-input" type="password" value={pwdForm.current}
                   onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })}
                   data-testid="current-password-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="new-pwd">New password</label>
            <input id="new-pwd" className="field-input" type="password" minLength={8} value={pwdForm.next}
                   onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })}
                   data-testid="new-password-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="confirm-pwd">Confirm new password</label>
            <input id="confirm-pwd" className="field-input" type="password" minLength={8} value={pwdForm.confirm}
                   onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                   data-testid="confirm-password-input" />
          </div>
        </div>
        {pwdMsg && (
          <p className={`mt-4 rounded-lg px-3 py-2 text-sm ${pwdMsg.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
             data-testid="change-password-message">
            {pwdMsg.text}
          </p>
        )}
        <button onClick={changePassword} disabled={pwdBusy || !pwdForm.current || pwdForm.next.length < 8}
                className="btn-primary mt-5" data-testid="change-password-submit">
          {pwdBusy ? "Updating…" : "Update password"}
        </button>
      </section>

      <section className="card-surface mt-6 p-6" data-testid="email-prefs-section">
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold">
          <Mail className="h-4 w-4 text-slate-500" /> Email notifications
        </h2>
        <p className="mt-1 text-sm text-slate-600">Choose which emails FindRxStaff sends you.</p>
        <div className="mt-4 space-y-3">
          {[
            ["shift_emails", "Shift confirmations and cancellations"],
            ["digest_emails", "Weekly Monday email of shifts matched to me"],
          ].map(([key, label]) => (
            <label key={key} className="flex cursor-pointer items-start gap-2.5 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 shrink-0 accent-[#1E3A8A]"
                checked={prefs[key]}
                onChange={(e) => savePrefs({ ...prefs, [key]: e.target.checked })}
                data-testid={`email-pref-${key.replace("_", "-")}`}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        {prefsSaved && (
          <p className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="email-prefs-saved">
            Email preferences saved.
          </p>
        )}
      </section>

      <section className="card-surface mt-6 p-6" data-testid="change-email-section">
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold">
          <Mail className="h-4 w-4 text-slate-500" /> Sign-in email
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Current email: <span className="font-medium text-slate-800">{user?.email}</span>
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="field-label" htmlFor="new-email">New email</label>
            <input
              id="new-email"
              className="field-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="change-email-input"
            />
          </div>
          <div>
            <label className="field-label" htmlFor="current-password">Current password</label>
            <input
              id="current-password"
              className="field-input"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              data-testid="change-email-password"
            />
          </div>
        </div>
        {emailMsg && (
          <p
            className={`mt-4 rounded-lg px-3 py-2 text-sm ${emailMsg.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
            data-testid="change-email-message"
          >
            {emailMsg.text}
          </p>
        )}
        <button
          onClick={changeEmail}
          disabled={emailBusy || !email}
          className="btn-primary mt-5"
          data-testid="change-email-submit"
        >
          {emailBusy ? "Updating…" : "Update email"}
        </button>
      </section>

      <section className="card-surface mt-6 border-red-200 p-6" data-testid="delete-account-section">
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-red-700">
          <Trash2 className="h-4 w-4" /> Delete my account
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Deleting your account removes or anonymizes your profile, availability, preferences and active
          expressions of interest. Certain historical records — past shifts, ratings, consent records and
          security logs — may be retained for legitimate business, legal, security or compliance reasons, so we
          cannot promise that every piece of data is permanently erased immediately.
        </p>
        {error && <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        {!confirm ? (
          <button onClick={() => setConfirm(true)} className="btn-ghost mt-5 border-red-200 text-red-700" data-testid="delete-account-button">
            Delete my account
          </button>
        ) : (
          <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4" data-testid="delete-confirm">
            <p className="text-sm font-medium text-red-800">This cannot be undone. Delete your account?</p>
            <div className="mt-4 flex gap-3">
              <button onClick={deleteAccount} disabled={busy} className="btn-primary bg-red-600 hover:bg-red-700" data-testid="delete-account-confirm">
                {busy ? "Deleting…" : "Yes, delete my account"}
              </button>
              <button onClick={() => setConfirm(false)} className="btn-ghost" data-testid="delete-account-cancel">
                Keep my account
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
